import { useMemo } from 'react';
import type { EventDto } from '@/types/dtos/events/response/event.dto';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import { PRODUCT_KEYS } from '@/types/dtos/orders/response/product.dto';
import { useProductsStore } from '@/state/stores/use-products-store';
import { useQuotaBalances } from '@/state/queries/entitlements/use-quota-balances';
import { getOriginEventId } from '@/helpers/event.helper';
import {
  findQuotaBalanceForScopedProductContext,
  resolveScopedProductBalanceContext,
} from '@/helpers/product-quota.helper';

export type InsufficientRemoteSpeakerDaysInfo = {
  remainingCredits: number;
  pendingDayCount: number;
  neededCredits: number;
};

export function useInsufficientRemoteSpeakerDaysInfo(
  event: EventDto | undefined,
  contributor: ContributorDto | undefined,
  selectedDayIds: string[],
): InsufficientRemoteSpeakerDaysInfo | null {
  const catalogProducts = useProductsStore((s) => s.products);
  const originEventId = event ? getOriginEventId(event) : undefined;
  const { data: balances } = useQuotaBalances('Event', originEventId);

  return useMemo(() => {
    const balanceCtx = resolveScopedProductBalanceContext(catalogProducts, PRODUCT_KEYS.EVENT_REMOTE_SPEAKER_DAY);
    if (!balanceCtx || !contributor || selectedDayIds.length === 0) return null;

    const dayBalance = findQuotaBalanceForScopedProductContext(balances, balanceCtx);
    const pendingDayIds = selectedDayIds.filter(
      (dayId) => !contributor.remoteSpeakerDays?.some((entry) => entry.dayId === dayId && entry.status === 'active'),
    );
    const remainingCredits = dayBalance?.available ?? 0;

    if (remainingCredits < pendingDayIds.length) {
      return {
        remainingCredits,
        pendingDayCount: pendingDayIds.length,
        neededCredits: pendingDayIds.length - remainingCredits,
      };
    }

    return null;
  }, [catalogProducts, balances, contributor, selectedDayIds]);
}
